import { API_CONFIG } from "../config/api";

const API_BASE_URL = API_CONFIG.BASE_URL;

export class BusinessUnitService {
  static async getAuthHeaders(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  static async createBusinessUnit(data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/business-units`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create business unit");
    }

    return response.json();
  }

  static async getBusinessUnitsForOrganization(
    organizationId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/organization/${organizationId}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch business units");
    }

    return response.json();
  }

  static async getBusinessUnitsForOrganizationSummary(
    organizationId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/organization/${organizationId}/summary`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch business units summary"
      );
    }

    return response.json();
  }

  // Get business units where user has ANY role (head or member) - RECOMMENDED for most use cases
  // This returns ALL business units where the user has any involvement
  static async getMyBusinessUnits(getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/business-units/my-units`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch your business units");
    }

    return response.json();
  }

  // Get business units where user is specifically a BUSINESS USER (member), not a head
  // Use this ONLY when you need to distinguish between head and member roles
  // Most applications should use getMyBusinessUnits() instead
  static async getMyBusinessUserUnits(getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/my-business-units`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error ||
          "Failed to fetch business units where you are a member"
      );
    }

    return response.json();
  }

  static async getBusinessUnitDetails(businessUnitId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch business unit details"
      );
    }

    return response.json();
  }

  // Role Assignment Methods
  static async assignBusinessUnitHead(
    businessUnitId,
    userId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/assign-business-unit-head`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to assign business unit head");
    }

    return response.json();
  }

  static async assignBusinessUser(
    businessUnitId,
    userId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/assign-business-user`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to assign business user");
    }

    return response.json();
  }

  static async removeBusinessUnitHead(
    businessUnitId,
    userId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/remove-business-unit-head/${userId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to remove business unit head");
    }

    return response.json();
  }

  static async removeBusinessUser(
    businessUnitId,
    userId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/remove-business-user/${userId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to remove business user");
    }

    return response.json();
  }

  static async getBusinessUnitHeads(businessUnitId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/business-unit-heads`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch business unit heads");
    }

    return response.json();
  }

  static async getBusinessUsers(businessUnitId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/business-units/${businessUnitId}/business-users`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch business users");
    }

    return response.json();
  }

  static async getAssignableUsers(
    organizationId,
    businessUnitId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);

    // Build query parameters
    const params = new URLSearchParams({
      organization_id: organizationId,
    });

    if (businessUnitId) {
      params.append("business_unit_id", businessUnitId);
    }

    const response = await fetch(
      `${API_BASE_URL}/users/assignable-users?${params}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch assignable users");
    }

    return response.json();
  }
}
